import { create } from 'zustand';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignIn,
  signInWithRedirect,
  fetchAuthSession,
} from 'aws-amplify/auth';

interface User {
  username: string;
  userId: string;
  attributes?: {
    email?: string;
    name?: string;
    'custom:group'?: string;
    'custom:tenant_id'?: string;
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  challengeName: string | null;
  tempUserEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  confirmNewPassword: (newPassword: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<boolean>;
  clearError: () => void;
  loginWithMicrosoft: () => Promise<void>;
  handleAuthCallback: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  challengeName: null,
  tempUserEmail: null,

  login: async (email, password) => {
    set({ loading: true, error: null, challengeName: null });
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      });

      if (isSignedIn) {
        // Get user details and attributes after successful sign in
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        const user: User = {
          username: currentUser.username,
          userId: currentUser.userId,
          attributes: attributes,
        };

        set({ user, isAuthenticated: true, loading: false, challengeName: null, tempUserEmail: null });
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        set({
          loading: false,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          tempUserEmail: email,
          error: null
        });
      } else {
        throw new Error('Sign in incomplete: ' + nextStep.signInStep);
      }
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        loading: false,
        isAuthenticated: false,
        user: null,
        challengeName: null,
        tempUserEmail: null
      });
      throw error;
    }
  },

  confirmNewPassword: async (newPassword: string, name?: string) => {
    set({ loading: true, error: null });
    try {
      const options = name
        ? {
            challengeResponse: newPassword,
            options: {
              userAttributes: {
                name: name,
              },
            },
          }
        : {
            challengeResponse: newPassword,
          };

      const { isSignedIn, nextStep } = await confirmSignIn(options);

      if (isSignedIn) {
        // Get user details after password confirmation
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        const user: User = {
          username: currentUser.username,
          userId: currentUser.userId,
          attributes: attributes,
        };

        set({
          user,
          isAuthenticated: true,
          loading: false,
          challengeName: null,
          tempUserEmail: null,
          error: null
        });
      } else {
        throw new Error('Password confirmation incomplete: ' + nextStep.signInStep);
      }
    } catch (error: any) {
      set({
        error: error.message || 'Password confirmation failed',
        loading: false
      });
      throw error;
    }
  },

  logout: async () => {
    set({ loading: true });
    try {
      await signOut();
      set({ user: null, isAuthenticated: false, loading: false, error: null });
    } catch (error: any) {
      set({ error: error.message || 'Logout failed', loading: false });
      throw error;
    }
  },

  checkSession: async () => {
    set({ loading: true });
    try {
      const session = await fetchAuthSession();

      if (!session.tokens) {
        set({ user: null, isAuthenticated: false, loading: false });
        return false;
      }

      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      const user: User = {
        username: currentUser.username,
        userId: currentUser.userId,
        attributes: attributes,
      };

      set({ user, isAuthenticated: true, loading: false });
      return true;
    } catch {
      set({ user: null, isAuthenticated: false, loading: false });
      return false;
    }
  },

  loginWithMicrosoft: async () => {
    set({ loading: true, error: null });
    try {
      await signInWithRedirect({
        provider: { custom: 'Microsoft' },
      });
      // User will be redirected to Microsoft login
      // Don't set loading to false here - page will reload
    } catch (error: any) {
      set({
        error: error.message || 'SSO login failed',
        loading: false,
      });
      throw error;
    }
  },

  handleAuthCallback: async () => {
    set({ loading: true, error: null });
    try {
      const session = await fetchAuthSession();

      if (session.tokens) {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        const user: User = {
          username: currentUser.username,
          userId: currentUser.userId,
          attributes: attributes,
        };

        set({
          user,
          isAuthenticated: true,
          loading: false,
          error: null,
        });
      } else {
        throw new Error('No valid session found');
      }
    } catch (error: any) {
      set({
        error: error.message || 'Authentication failed',
        loading: false,
        isAuthenticated: false,
        user: null,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
